import axios from 'axios';
import { config } from '../../config/Config';
import { buildSseHeaders } from '../../config/sseHeaders.js';
import { apiErrorMessage } from '@/utils/apiError.js';
import { turnSettled } from '@/components/chat/chatCredits.js';

/**
 * Client for the streaming AI chat gateway (geo-play-web {@code /v1/web/chat}, which proxies
 * geo-play-intel's {@code /chat/stream} through IntelChatClient).
 *
 * The gateway is shared by every AI surface, not just segments: intel picks the agent that answers
 * from {@code metadata.page} (and, for a knowledge chat, {@code metadata.scope}); without a page it
 * falls back to matching {@code metadata.currentPage}. So this stays page-agnostic and each caller
 * declares its own page key.
 *
 * Frames intel emits (see geo-play-intel {@code api/rest/v1/chat.py} and the entity tools):
 *   chat_started      {conv_id, request_id, chat_name, protocol}
 *   token             {token, step_id}                  — streamed answer text
 *   step              {id, parent_id, kind, status, …}   — tool / thinking trace
 *   segment_proposal  {segment, ready, missing_inputs, …}— structured draft to diff + apply
 *   completed         {response, conv_id, message_id, …}  — message_id: the saved answer, what a like rates
 *   error | cancelled
 */

/** Frame delimiter: SSE separates events with a blank line. */
const FRAME_SEP = /\r?\n\r?\n/;

/**
 * Parse one SSE frame into {event, data}.
 *
 * Spring's SSE writer omits the space after {@code event:} / {@code data:}, so the separator is
 * matched loosely. Per the SSE spec a frame may carry several {@code data:} lines that concatenate
 * with newlines — intel sends one, but honoring the spec costs nothing and stops a multi-line
 * payload from being silently truncated to its first line.
 */
const parseFrame = (block) => {
    let event = null;
    const dataLines = [];
    for (const line of block.split(/\r?\n/)) {
        if (line.startsWith(':')) continue;                 // comment / keep-alive
        const match = /^(event|data):\s*(.*)$/.exec(line);
        if (!match) continue;
        if (match[1] === 'event') event = match[2].trim();
        else dataLines.push(match[2]);
    }
    if (!dataLines.length) return null;
    const raw = dataLines.join('\n');
    let data;
    try {
        data = JSON.parse(raw);
    } catch {
        data = raw;
    }
    return { event: event || 'message', data };
};

/**
 * Send one chat turn and consume the reply as server-sent events, invoking
 * {@code onEvent(event, data)} per frame.
 *
 * @param page        The chat that answers: intel's page key ("player_segments", "remote_config",
 *                    "live_event_knowledge", or "main"). Preferred over currentPage.
 * @param scope       What a knowledge chat is about: {system, systemId, parents?}, e.g.
 *                    {system: 'LIVE_EVENT', systemId: eventId}. Web checks it before forwarding.
 * @param currentPage Route path or page key, kept for context and intel's legacy routing.
 * @param context     Snapshot of what is on screen, forwarded verbatim as the turn's page context.
 * @param subject     The record the conversation is about, {type, id, parentId?} (e.g. a dashboard, or a
 *                    chart with its dashboard as parent). Intel stamps it on a new conversation, so the
 *                    chat's history can be listed per record.
 * @param oneShot     True for a single generation: kept by intel, but not titled or listed as a chat.
 * @param signal      AbortSignal to stop consuming mid-stream.
 */
// A refused stream as the user should read it. The body is web's error JSON ({ message, detailedMessage, error, path }
// or the AI usage gate's), read the way every other API error is (apiErrorMessage); never shown raw. A body that isn't
// JSON is shown only when it is a short plain line - an HTML page (a proxy's or Cloud Armor's) never is. The status and
// parsed body ride on the error as `response`, so callers can tell a 403 or an AI limit apart.
export const streamError = (status, body) => {
    const text = String(body ?? '').trim();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }
    const fallback = status >= 500
        ? 'The assistant couldn’t answer just now. Please try again.'
        : `The assistant couldn’t answer (error ${status}).`;
    const plain = !data && text && text.length <= 200 && !/[<>{}]/.test(text) ? text : null;
    const message = data && typeof data === 'object'
        ? apiErrorMessage({ response: { status, data } }, fallback)
        : plain || fallback;
    const err = new Error(message);
    err.response = { status, data };
    return err;
};

export const streamChat = async (
    {
        message, convId, page, scope, currentPage, context, gameId, role, debugMode, subject, oneShot,
    },
    onEvent,
    { signal } = {},
) => {
    const headers = await buildSseHeaders({ gameId });
    const body = {
        message,
        conv_id: convId || undefined,
        role: role || undefined,
        debugMode: debugMode || undefined,
        metadata: {
            currentPage,
            context: context || '',
            page: page || undefined,
            scope: scope || undefined,
            subject: subject || undefined,
            oneShot: oneShot || undefined,
        },
    };

    let response;
    try {
        response = await fetch(`${config.serverBaseUrl}/v1/web/chat/stream`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            signal,
        });
    } catch (err) {
        if (err?.name === 'AbortError') return;   // Stopped before headers arrived — a clean end.
        throw err;
    }

    if (!response.ok) {
        const body = await response.text().catch(() => '');
        // Refused (out of credits, a limit): the ring may be out of date.
        turnSettled(null);
        throw streamError(response.status, body);
    }
    if (!response.body) throw new Error('Chat stream returned no body.');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // `completed` tells the chats' credits ring and token line what the answer cost (chatCredits.js), first, while
    // the chat that asked still shows itself busy.
    let settled = false;
    const dispatch = (block) => {
        const frame = parseFrame(block);
        if (!frame) return;
        if (frame.event === 'completed' && !settled) {
            settled = true;
            turnSettled(frame.data ?? null);
        }
        onEvent(frame.event, frame.data);
    };

    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const blocks = buffer.split(FRAME_SEP);
            buffer = blocks.pop() ?? '';
            for (const block of blocks) {
                if (!block.trim()) continue;
                dispatch(block);
                // Yield to the event loop so React can paint between frames.
                await new Promise((resolve) => { setTimeout(resolve, 0); });
            }
        }
        // A stream that ends without a trailing blank line leaves its last frame in the buffer.
        // That frame is usually `completed`, so dropping it would strand the UI mid-stream.
        if (buffer.trim()) dispatch(buffer);
    } catch (err) {
        if (err?.name === 'AbortError') return;   // Stopped mid-stream — a clean end.
        throw err;
    } finally {
        reader.releaseLock?.();
        // Stopped or failed: nothing to show for it, but it may still have spent.
        if (!settled) turnSettled(null);
    }
};

/**
 * Ask the backend to abandon an in-flight run (the {@code request_id} from the chat_started frame).
 *
 * Aborting the reader only stops this client listening; intel keeps executing — and paying for —
 * the turn until it is told to stop. Best-effort: the stream is already being torn down, so a
 * failure here has nothing left to report to.
 */
export const cancelChatStream = async (requestId) => {
    if (!requestId) return;
    try {
        await axios.post(`${config.serverBaseUrl}/v1/web/chat/stream/cancel`, { request_id: requestId });
    } catch (err) {
        console.error('cancelChatStream failed:', err.response?.data || err.message);
    }
};
